/** Public Landing route: server snapshot → sealed seven-chapter product story. */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  landingProductDescriptorBuilder,
  type LandingAuthIntent,
  type LandingProductCallbacks,
} from './buildLandingProductDescriptor';
import { resolveLandingProductCopy } from './landingProductCopy';
import { buildLandingOwnerHandoff } from './landingOwnerHandoff';
import { landingHeroForecastForProperty } from './landingHeroForecastData';
import { useLandingStorySnapshot } from './useLandingStorySnapshot';
import { useLandingTimberPriceSeries } from './useLandingTimberPriceSeries';
import { FmProductPage } from '@/components/ui/archetypes';
import { useAuth } from '@/hooks/useAuth';
import { useLandingHandoffContext } from '@/hooks/useLandingHandoffContext';
import { useLanguage, type Language } from '@/i18n';
import { storySessionKey } from '@/queries/storySessionKey';
import { usePropertyForestContext } from '@/hooks/usePropertyForestContext';
import { usePublishPropertyForestSnapshot } from '@/hooks/usePublishPropertyForestSnapshot';
import { buildMeasurementPropertyMapData } from '@/components/measurements/measurementMapData';

import { appRoutePath, appRoutePatterns } from '@/shared/navigation/appRoutePatterns';
function selectedSkuOwnerReturnPath(skuId: string, propertyId: string): string {
  const params = { propertyId };
  const myForestsWithProperty = appRoutePath(
    appRoutePatterns.myForests,
    {},
    new URLSearchParams({ property: propertyId }),
  );
  const paths: Record<string, string> = {
    offer_decode: appRoutePath(appRoutePatterns.offerDecode, params),
    buyer_decode: appRoutePath(appRoutePatterns.priceEstimate, params, 'mode=buyer'),
    plot_package: myForestsWithProperty,
    portfolio_estate: myForestsWithProperty,
  };
  const path = paths[skuId];
  if (!path) throw new Error(`Landing received unsupported catalog SKU: ${skuId}`);
  return path;
}

const DEFAULT_PROPERTY_ID = '178-415-5-2';
/** `/pricing` hashes the paywall CTAs open, mapped to their price card section. */
const PRICING_HASH_TARGETS: Readonly<Record<string, string>> = {
  '#plot-package': 'landing-catalog-plot_package',
};

interface LandingRouteState {
  openLogin?: boolean;
  authRequired?: boolean;
  from?: { pathname?: string; search?: string; hash?: string };
  pendingAuthRoute?: string;
}

function routeFromState(state: LandingRouteState): string | null {
  const from = state.from;
  if (!from?.pathname?.startsWith('/')) return null;
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

// The route hook keeps auth handoff, query state, and descriptor callbacks in one lifecycle boundary.
// eslint-disable-next-line max-lines-per-function
const LandingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const copy = React.useMemo(() => resolveLandingProductCopy(t), [t]);
  const routeProperty = new URLSearchParams(location.search).get('property');
  const propertyId = routeProperty?.trim() || DEFAULT_PROPERTY_ID;
  const inboundContext = useLandingHandoffContext(location.search);
  const [searchValue, setSearchValue] = React.useState(routeProperty ?? '');
  const [showcaseSelectedStandId, setShowcaseSelectedStandId] = React.useState<string | null>(null);
  const [simulationLensId, setSimulationLensId] = React.useState('value');
  const [heroForecastMetric, setHeroForecastMetric] = React.useState<'growth' | 'harvest-income'>('growth');
  const [authDialog, setAuthDialog] = React.useState<{
    open: boolean;
    mode: 'login' | 'register';
    intent: LandingAuthIntent | null;
  }>(
    { open: false, mode: 'login', intent: null },
  );
  const pendingRoute = React.useRef<string | null>(null);
  const pendingRevealProperty = React.useRef<string | null>(null);
  const replacePendingRouteState = React.useCallback((target: string | null) => {
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: target ? { pendingAuthRoute: target } satisfies LandingRouteState : null,
    });
  }, [location.hash, location.pathname, location.search, navigate]);
  React.useEffect(() => {
    setSearchValue(routeProperty ?? '');
  }, [routeProperty]);
  React.useEffect(() => {
    const state = (location.state ?? {}) as LandingRouteState;
    if (!state.openLogin && !state.authRequired) return;
    const target = routeFromState(state);
    pendingRoute.current = target;
    setAuthDialog({ open: true, mode: 'login', intent: null });
    replacePendingRouteState(target);
  }, [location.state, replacePendingRouteState]);
  React.useEffect(() => {
    if (location.pathname !== '/pricing') return;
    // Paywall CTAs open a named price card; on a narrow viewport the chapter
    // top shows only the free card, so the card itself is the scroll target.
    const target = document.getElementById(PRICING_HASH_TARGETS[location.hash] ?? 'start');
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'start' });
    }
  }, [location.hash, location.pathname]);
  const query = useLandingStorySnapshot(propertyId, storySessionKey(auth.user?.id), inboundContext.decodeJobId);
  const forestContext = usePropertyForestContext(propertyId, 50, true, {
    omitAuthorization: true,
  });
  const matchingForestContext = forestContext.context?.propertyId === propertyId
    ? forestContext.context
    : null;
  const timberPriceQuery = useLandingTimberPriceSeries(
    propertyId,
    matchingForestContext,
    language,
  );
  const propertyForestSnapshot = React.useMemo(() => ({
    context: matchingForestContext,
    isLoading: forestContext.isLoading,
    error: forestContext.error,
  }), [forestContext.error, forestContext.isLoading, matchingForestContext]);
  usePublishPropertyForestSnapshot(propertyId, propertyForestSnapshot);
  const showcaseMapData = React.useMemo(
    () => buildMeasurementPropertyMapData(propertyId, matchingForestContext, forestContext.isLoading),
    [forestContext.isLoading, matchingForestContext, propertyId],
  );
  React.useEffect(() => setShowcaseSelectedStandId(null), [propertyId]);
  React.useEffect(() => {
    const firstPictureStatus = query.data?.firstPicture.status;
    const canReveal = firstPictureStatus === 'available'
      || firstPictureStatus === 'partial'
      || firstPictureStatus === 'stale';
    if (pendingRevealProperty.current !== propertyId || !canReveal) return;
    const result = document.getElementById('landing-feature-showcase');
    if (typeof result?.scrollIntoView !== 'function') return;
    pendingRevealProperty.current = null;
    result.scrollIntoView({ block: 'start' });
  }, [propertyId, query.data?.firstPicture.status]);
  const openAfterAuth = React.useCallback((
    target: string,
    mode: 'login' | 'register' = 'register',
    intent: LandingAuthIntent | null = null,
  ) => {
    if (auth.isAuthenticated) {
      navigate(target);
      return;
    }
    pendingRoute.current = target;
    replacePendingRouteState(target);
    setAuthDialog({ open: true, mode, intent });
  }, [auth.isAuthenticated, navigate, replacePendingRouteState]);
  const revealSection = React.useCallback((sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ block: 'start' });
  }, []);
  const ownerRoute = React.useCallback((
    destination: Parameters<typeof buildLandingOwnerHandoff>[0]['destination'],
    skuId?: string,
    returnTo = `${location.pathname}${location.search}${location.hash}`,
  ) => buildLandingOwnerHandoff({
    destination,
    propertyId,
    story: query.data ?? null,
    inboundContext,
    returnTo,
    skuId,
  }), [inboundContext, location.hash, location.pathname, location.search, propertyId, query.data]);
  const callbacks = React.useMemo<LandingProductCallbacks>(() => ({
    searchChange: setSearchValue,
    searchSubmit: (value) => {
      const selected = value.trim();
      if (!selected) {
        pendingRevealProperty.current = null;
        navigate(location.pathname);
        return;
      }
      pendingRevealProperty.current = selected;
      navigate(`${location.pathname}?property=${encodeURIComponent(selected)}`);
      if (selected === propertyId && ['available', 'partial', 'stale'].includes(
        query.data?.firstPicture.status ?? '',
      )) {
        window.requestAnimationFrame(() => {
          document.getElementById('landing-feature-showcase')?.scrollIntoView({ block: 'start' });
          pendingRevealProperty.current = null;
        });
      }
    },
    addForest: () => openAfterAuth(ownerRoute('browse'), 'register', 'free'),
    openMeasurements: () => openAfterAuth(ownerRoute('measurements')),
    openOperations: () => openAfterAuth(ownerRoute('operations')),
    openBuyerValuation: () => openAfterAuth(appRoutePath(appRoutePatterns.priceEstimate, { propertyId }, 'mode=buyer'), 'register', 'buyer_decode'),
    openOwnerValuation: () => openAfterAuth(appRoutePath(appRoutePatterns.priceEstimate, { propertyId })),
    openImplement: () => openAfterAuth(ownerRoute('implement')),
    openMonitoring: () => openAfterAuth(ownerRoute('watch')),
    selectSku: (skuId) => openAfterAuth(ownerRoute(
      'settings',
      skuId,
      inboundContext.returnTo ?? selectedSkuOwnerReturnPath(skuId, propertyId),
    ), 'register', skuId as LandingAuthIntent),
    retry: () => { void query.refetch(); },
    home: () => navigate('/'),
    login: () => {
      pendingRoute.current = null;
      replacePendingRouteState(null);
      setAuthDialog({ open: true, mode: 'login', intent: null });
    },
    register: () => {
      pendingRoute.current = null;
      replacePendingRouteState(null);
      setAuthDialog({ open: true, mode: 'register', intent: null });
    },
    dashboard: () => navigate(appRoutePatterns.map),
    logout: auth.logout,
    languageChange: (nextLanguage: Language) => setLanguage(nextLanguage),
    closeAuth: () => {
      pendingRoute.current = null;
      replacePendingRouteState(null);
      setAuthDialog((dialog) => ({ ...dialog, open: false, intent: null }));
    },
    authenticated: () => {
      const target = pendingRoute.current ?? appRoutePatterns.map;
      pendingRoute.current = null;
      navigate(target, { replace: true, state: null });
    },
    selectShowcaseStand: setShowcaseSelectedStandId,
    selectHeroForecastMetric: setHeroForecastMetric,
    selectSimulationLens: setSimulationLensId,
    revealPlanningProof: () => revealSection('landing-planning-proof'),
    revealOfferPricing: () => revealSection('landing-catalog-offer_decode'),
    revealBuyerPricing: () => revealSection('landing-catalog-buyer_decode'),
  }), [
    auth.logout,
    inboundContext.returnTo,
    location.pathname,
    navigate,
    openAfterAuth,
    ownerRoute,
    propertyId,
    query,
    replacePendingRouteState,
    revealSection,
    setLanguage,
  ]);
  const descriptor = React.useMemo(() => landingProductDescriptorBuilder.build({
    story: query.data ?? null,
    loading: query.isPending,
    refreshing: query.isFetching && query.data !== undefined,
    error: query.error instanceof Error ? query.error.message : query.isError ? copy.state.error : null,
    locale: language,
    searchValue,
    copy,
    identity: {
      authenticated: auth.isAuthenticated,
      accountLabel: auth.user?.first_name || auth.user?.username || copy.chrome.account,
    },
    authDialog,
    showcaseMapData,
    showcaseSelectedStandId,
    heroForecast: landingHeroForecastForProperty(propertyId),
    heroForecastMetric,
    simulationLensId,
    timberPrices: {
      data: timberPriceQuery.data ?? null,
      loading: timberPriceQuery.isPending,
      error: timberPriceQuery.error instanceof Error ? timberPriceQuery.error.message : null,
    },
  }, callbacks), [
    auth.isAuthenticated,
    auth.user?.first_name,
    auth.user?.username,
    authDialog,
    callbacks,
    copy,
    language,
    query.data,
    query.error,
    query.isError,
    query.isFetching,
    query.isPending,
    propertyId,
    searchValue,
    showcaseMapData,
    showcaseSelectedStandId,
    heroForecastMetric,
    simulationLensId,
    timberPriceQuery.data,
    timberPriceQuery.error,
    timberPriceQuery.isPending,
  ]);
  return <FmProductPage {...descriptor} />;
};

export default LandingPage;
